import { Area, FlowStep } from 'types/checkout';

import { checkoutFlowRaw as RentalCheckoutFlowRaw } from 'components/Checkout/rental/flow';
import { checkoutFlowRaw as ProgramCheckoutFlowRaw } from 'components/Checkout/program/flow';
import { checkoutFlowRaw as MembershipCheckoutFlowRaw } from 'components/Checkout/membership/flow';
import { checkoutFlowRaw as LeagueCheckoutFlowRaw } from 'components/Checkout/league/flow';
import { useSteps } from './useSteps';

enum FollowUpStepenum {
	prev = 'prev',
	next = 'next',
}

export interface ProgressStep {
	tabName: string;
	urlPath: string;
	hidden?: boolean;
}

interface CheckoutFlowRaw {
	[area: string]: FlowStep[];
}

export const checkoutFlow: CheckoutFlowRaw = {
	rental: RentalCheckoutFlowRaw,
	program: ProgramCheckoutFlowRaw,
	membership: MembershipCheckoutFlowRaw,
	league: LeagueCheckoutFlowRaw,
};

const useStepsAndIndex = (area: Area, urlPath: string | undefined) => {
	const steps = useSteps(area);
	const currStepIndex = steps.findIndex(step => step.urlPath === urlPath);
	return { steps, currStepIndex };
};

/**
 *
 * @param steps array of relevant FlowStep
 * @param currStepIndex the index of where the user currently is at the moment
 * @param goToStep the direction of the steps the user wants to go i.e. to the next one, or back to the perevious one
 * @returns a number indication how many indexs to advance in the desired direction of the steps array. 1 as normal, 2 as to skip the adjacent step.
 */
const getNextStepIndex = (steps: FlowStep[], currStepIndex: number, goToStep: FollowUpStepenum) => {
	const goToIndex = goToStep === FollowUpStepenum.next ? currStepIndex + 1 : currStepIndex - 1;
	const stepSlice = steps.slice(goToIndex);
	let skipCount = 0;
	for (const step of stepSlice) {
		if (!step.isDisabled) {
			break;
		}
		skipCount++;
	}
	return skipCount + 1;
};

function GetPrevStep(area: Area, urlPath: string | undefined) {
	const { steps, currStepIndex } = useStepsAndIndex(area, urlPath);

	const nextStepIndex = getNextStepIndex(steps, currStepIndex, FollowUpStepenum.prev);
	return steps[currStepIndex - nextStepIndex] || null;
}

function GetNextStep(area: Area, urlPath: string | undefined) {
	const { steps, currStepIndex } = useStepsAndIndex(area, urlPath);
	const nextStepIndex = getNextStepIndex(steps, currStepIndex, FollowUpStepenum.next);
	return steps[currStepIndex + nextStepIndex] || null;
}

export function getStepIndex(area: Area, urlPath: string | undefined) {
	return checkoutFlow[area].findIndex(step => step.urlPath === urlPath);
}

export function findPrevNextStep(area: Area, urlPath: string | undefined) {
	const result: Record<string, ProgressStep | null> = {
		prevStep: GetPrevStep(area, urlPath),
		nextStep: GetNextStep(area, urlPath),
	};

	return result;
}
